import { useEffect, useMemo, useState } from "react"
import { Link } from "../components/link"
import { SiteHeader } from "../components/site-header"
import {
  apiGet,
  apiPost,
  type PublicLaunchPreview,
  type PublicLaunchPreviewAsset,
} from "../lib/api"

type Stage = "idle" | "syncing" | "copy" | "media" | "ready"

export function LaunchPage({ search }: { search: string }) {
  const params = new URLSearchParams(search)
  const previewId = params.get("preview")
  const [websiteUrl, setWebsiteUrl] = useState("")
  const [locationText, setLocationText] = useState("")
  const [autoOptimize, setAutoOptimize] = useState(true)
  const [preview, setPreview] = useState<PublicLaunchPreview | null>(null)
  const [stage, setStage] = useState<Stage>("idle")
  const [visibleCount, setVisibleCount] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [checkingOut, setCheckingOut] = useState(false)

  useEffect(() => {
    if (!previewId) return
    void apiGet<PublicLaunchPreview>(`/api/public/launch-previews/${previewId}`)
      .then((loaded) => {
        setPreview(loaded)
        setWebsiteUrl(loaded.websiteUrl)
        setLocationText(loaded.locationText ?? "")
        setStage("ready")
        setVisibleCount(loaded.assets.length)
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Could not load preview.")
      )
  }, [previewId])

  useEffect(() => {
    if (!preview) return
    setVisibleCount(0)
    const timers = [
      window.setTimeout(() => setStage("copy"), 350),
      window.setTimeout(() => setVisibleCount(3), 700),
      window.setTimeout(() => setVisibleCount(8), 1200),
      window.setTimeout(() => setStage("media"), 1700),
      window.setTimeout(() => setVisibleCount(preview.assets.length), 2300),
      window.setTimeout(() => setStage("ready"), 2800),
    ]
    return () => timers.forEach(window.clearTimeout)
  }, [preview])

  const visibleAssets = useMemo(
    () => (preview ? preview.assets.slice(0, visibleCount) : []),
    [preview, visibleCount]
  )

  async function buildPreview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setPreview(null)
    setVisibleCount(0)
    setStage("syncing")
    try {
      const created = await apiPost<PublicLaunchPreview>("/api/public/launch-previews", {
        websiteUrl,
        locationText: locationText.trim() || undefined,
        autoOptimize,
        goal: "leads",
      })
      window.history.replaceState(
        null,
        "",
        `/launch?preview=${encodeURIComponent(created.previewId)}`
      )
      setPreview(created)
    } catch (reason) {
      setStage("idle")
      setError(reason instanceof Error ? reason.message : "Could not build preview.")
    }
  }

  async function launchNow() {
    if (!preview) return
    setCheckingOut(true)
    setError(null)
    try {
      const result = await apiPost<{ url: string }>(
        `/api/public/launch-previews/${preview.previewId}/checkout`
      )
      window.location.assign(result.url)
    } catch (reason) {
      setCheckingOut(false)
      setError(reason instanceof Error ? reason.message : "Could not start checkout.")
    }
  }

  return (
    <div className="launch-page">
      <SiteHeader />
      <main className="launch-builder">
        <section className="launch-hero">
          <div className="launch-hero__copy">
            <div className="eyebrow">Public campaign preview</div>
            <h1>See your first campaign before you create an account.</h1>
            <p>
              Enter your website. Adgiro reads it, writes the launch copy, drafts keyword themes,
              and prepares image concepts while you watch.
            </p>
          </div>
          <form className="launch-form" onSubmit={buildPreview}>
            <label>
              <span>Website</span>
              <input
                required
                placeholder="example.com"
                value={websiteUrl}
                onChange={(event) => setWebsiteUrl(event.target.value)}
              />
            </label>
            <label>
              <span>Location</span>
              <input
                placeholder="Los Angeles, nationwide, online"
                value={locationText}
                onChange={(event) => setLocationText(event.target.value)}
              />
            </label>
            <label className="launch-toggle">
              <input
                checked={autoOptimize}
                type="checkbox"
                onChange={(event) => setAutoOptimize(event.target.checked)}
              />
              <span>Auto Optimize</span>
            </label>
            <button
              className="button"
              disabled={stage !== "idle" && stage !== "ready"}
              type="submit"
            >
              {stage === "idle" || stage === "ready" ? "Build my preview" : "Building..."}
            </button>
            {error ? <div className="launch-error">{error}</div> : null}
          </form>
        </section>

        <section className="launch-preview-shell">
          <div className="launch-progress" aria-live="polite">
            <ProgressStep
              active={stage === "syncing"}
              done={Boolean(preview)}
              label="Syncing your site"
            />
            <ProgressStep active={stage === "copy"} done={visibleCount >= 8} label="Writing ads" />
            <ProgressStep
              active={stage === "media"}
              done={stage === "ready"}
              label="Rendering media"
            />
          </div>

          {preview ? (
            <div className="launch-preview-grid">
              <article className="launch-summary">
                <span>Campaign angle</span>
                <h2>{preview.summary.businessName}</h2>
                <p>{preview.summary.offer}</p>
                <div className="launch-summary__detail">
                  <strong>{preview.summary.angle}</strong>
                  <span>{preview.summary.audience}</span>
                </div>
              </article>

              <AssetColumn
                title="Search copy"
                assets={visibleAssets}
                types={["headline", "description"]}
              />
              <AssetColumn
                title="Keyword themes"
                assets={visibleAssets}
                types={["keyword_theme", "negative_keyword"]}
              />
              <ImagePreview assets={visibleAssets.filter((asset) => asset.type === "image")} />
            </div>
          ) : (
            <div className="launch-empty-preview">
              <div>
                <span>Preview appears here</span>
                <strong>Sync your site to start the reveal.</strong>
              </div>
            </div>
          )}
        </section>
      </main>

      <aside className="launch-checkout-bar">
        <div>
          <span>Adgiro Starter</span>
          <strong>$500/mo</strong>
          <small>$400 ad spend + $100 management</small>
        </div>
        <button
          className="button"
          disabled={!preview || checkingOut}
          type="button"
          onClick={launchNow}
        >
          {checkingOut ? "Opening checkout..." : "Launch Now"}
        </button>
      </aside>
    </div>
  )
}

export function LaunchSuccessPage({ search }: { search: string }) {
  const sessionId = new URLSearchParams(search).get("session_id")
  const [message, setMessage] = useState("Activating your launch.")

  useEffect(() => {
    if (!sessionId) {
      setMessage("Checkout session is missing.")
      return
    }
    void apiPost<{ redirectPath: string }>("/api/public/launch/complete", { sessionId })
      .then((result) => {
        setMessage("Opening your campaign panel.")
        window.location.assign(result.redirectPath)
      })
      .catch((reason: unknown) => {
        setMessage(reason instanceof Error ? reason.message : "Could not activate launch.")
      })
  }, [sessionId])

  return (
    <div className="launch-page">
      <SiteHeader />
      <main className="launch-success">
        <div className="launch-success__pulse" />
        <h1>{message}</h1>
        <p>
          Your account is created after payment. Adgiro is importing the preview and preparing the
          managed ads account setup.
        </p>
        <Link className="button button--ghost" href="/launch">
          Back to launch builder
        </Link>
      </main>
    </div>
  )
}

function ProgressStep({ active, done, label }: { active: boolean; done: boolean; label: string }) {
  return (
    <div className={`launch-progress__step${active ? " is-active" : ""}${done ? " is-done" : ""}`}>
      <span />
      <strong>{label}</strong>
    </div>
  )
}

function AssetColumn({
  title,
  assets,
  types,
}: {
  title: string
  assets: PublicLaunchPreviewAsset[]
  types: PublicLaunchPreviewAsset["type"][]
}) {
  const filtered = assets.filter((asset) => types.includes(asset.type))
  return (
    <article className="launch-asset-column">
      <h3>{title}</h3>
      <div className="launch-asset-list">
        {filtered.map((asset) => (
          <div className={`launch-asset launch-asset--${asset.type}`} key={asset.id}>
            {asset.text}
          </div>
        ))}
      </div>
    </article>
  )
}

function ImagePreview({ assets }: { assets: PublicLaunchPreviewAsset[] }) {
  return (
    <article className="launch-image-preview">
      <h3>Image concepts</h3>
      <div className="launch-image-grid">
        {[0, 1, 2].map((index) => {
          const asset = assets[index]
          return (
            <div className={`launch-image-card${asset ? " is-ready" : ""}`} key={String(index)}>
              <span>{asset?.title ?? "Rendering"}</span>
              <strong>ADGIRO PREVIEW</strong>
            </div>
          )
        })}
      </div>
    </article>
  )
}
